import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { usePricesLastUpdated } from '../../hooks/useCalculate';

interface NavItem {
  label: string;
  icon: string;
  path: string;
}

interface CalcGroup {
  label: string;
  icon: string;
  items: NavItem[];
}

function NavLink({ item, isActive }: { item: NavItem; isActive: boolean }) {
  return (
    <Link
      to={item.path}
      discover="none"
      className={
        isActive
          ? 'border-l-4 border-[#0078d4] flex items-center gap-3 px-6 py-4 transition-all'
          : 'flex items-center gap-3 px-6 py-4 transition-all'
      }
      style={{
        backgroundColor: isActive ? 'var(--surface-container-high)' : 'transparent',
        color: isActive ? 'var(--nav-active-text)' : 'var(--text-secondary)',
      }}
      onMouseEnter={(e) => {
        if (!isActive) e.currentTarget.style.backgroundColor = 'var(--surface-container-high)';
      }}
      onMouseLeave={(e) => {
        if (!isActive) e.currentTarget.style.backgroundColor = 'transparent';
      }}
    >
      <span className="material-symbols-outlined text-[20px]">{item.icon}</span>
      <span className="font-['Inter'] uppercase tracking-widest text-[10px] font-bold">
        {item.label}
      </span>
    </Link>
  );
}

function CalcGroupSection({ group, activePathname, isOpen, onToggle }: {
  group: CalcGroup;
  activePathname: string;
  isOpen: boolean;
  onToggle: () => void;
}) {
  return (
    <div>
      <button
        onClick={onToggle}
        aria-expanded={isOpen}
        aria-label={`${isOpen ? 'Collapse' : 'Expand'} ${group.label}`}
        className="w-full flex items-center gap-3 px-6 py-3 transition-all"
        style={{
          color: 'var(--text-tertiary)',
          backgroundColor: 'transparent'
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.color = 'var(--text-secondary)';
          e.currentTarget.style.backgroundColor = 'var(--surface-container-low)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.color = 'var(--text-tertiary)';
          e.currentTarget.style.backgroundColor = 'transparent';
        }}
      >
        <span className="material-symbols-outlined text-[18px]">{group.icon}</span>
        <span className="font-['Inter'] uppercase tracking-widest text-[10px] font-bold flex-1 text-left">
          {group.label}
        </span>
        <span className="material-symbols-outlined text-[16px] transition-transform" style={{ transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)' }}>
          expand_more
        </span>
      </button>

      {isOpen && (
        <div className="flex flex-col pl-4 ml-6" style={{ borderLeft: '1px solid var(--surface-container-high)' }}>
          {group.items.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              discover="none"
              className={
                activePathname === item.path
                  ? 'flex items-center gap-2 px-4 py-2.5 border-l-2 border-[#0078d4] -ml-px transition-all text-[11px]'
                  : 'flex items-center gap-2 px-4 py-2.5 border-l-2 border-transparent -ml-px transition-all text-[11px]'
              }
              style={{
                color: activePathname === item.path ? 'var(--nav-active-text)' : 'var(--text-tertiary)',
              }}
              onMouseEnter={(e) => {
                if (activePathname !== item.path) e.currentTarget.style.color = 'var(--text-secondary)';
              }}
              onMouseLeave={(e) => {
                if (activePathname !== item.path) e.currentTarget.style.color = 'var(--text-tertiary)';
              }}
            >
              <span className="material-symbols-outlined text-[15px]">{item.icon}</span>
              <span className="font-['Inter'] leading-snug">{item.label}</span>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

interface SidebarProps {
  mobileOpen?: boolean;
  onMobileClose?: () => void;
}

const COLLAPSE_STORAGE_KEY = 'sidebar-calc-collapsed';

export default function Sidebar({ mobileOpen = false, onMobileClose }: SidebarProps) {
  const location = useLocation();
  const { data: pricesData } = usePricesLastUpdated();
  const pricesUpdated = pricesData?.last_updated
    ? new Date(pricesData.last_updated).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
    : null;

  // Initialize collapsed state from localStorage
  const [collapsedGroups, setCollapsedGroups] = useState<Record<string, boolean>>(() => {
    const stored = localStorage.getItem(COLLAPSE_STORAGE_KEY);
    return stored ? JSON.parse(stored) : {};
  });

  // Persist collapsed state to localStorage
  useEffect(() => {
    localStorage.setItem(COLLAPSE_STORAGE_KEY, JSON.stringify(collapsedGroups));
  }, [collapsedGroups]);

  // Build calculator groups from CALCULATORS config
  const calcGroups: CalcGroup[] = [
    {
      label: 'Compute',
      icon: 'desktop_mac',
      items: [
        { label: 'App Service', icon: 'language', path: '/app-service' },
        { label: 'Azure Functions', icon: 'functions', path: '/functions' },
        { label: 'Virtual Machines', icon: 'computer', path: '/virtual-machines' },
        { label: 'Azure Kubernetes Service', icon: 'router', path: '/aks' },
        { label: 'Virtual Desktop', icon: 'smart_display', path: '/virtual-desktop' },
      ],
    },
    {
      label: 'Storage & Data',
      icon: 'storage',
      items: [
        { label: 'Blob Storage', icon: 'cloud', path: '/blob-storage' },
        { label: 'Managed Disks', icon: 'save', path: '/managed-disks' },
        { label: 'Azure Backup', icon: 'shield_backup', path: '/azure-backup' },
        { label: 'SQL Database', icon: 'database', path: '/sql-database' },
        { label: 'SQL Managed Instance', icon: 'database', path: '/sql-managed-instance' },
        { label: 'Data Factory', icon: 'data_usage', path: '/data-factory' },
        { label: 'Azure OpenAI', icon: 'smart_toy', path: '/azure-openai' },
      ],
    },
    {
      label: 'Networking',
      icon: 'cloud_sync',
      items: [
        { label: 'Bandwidth & Egress', icon: 'cloud_download', path: '/bandwidth' },
        { label: 'Networking', icon: 'hub', path: '/networking' },
        { label: 'Azure Firewall', icon: 'security', path: '/firewall' },
        { label: 'Azure Front Door', icon: 'account_tree', path: '/front-door' },
        { label: 'Application Gateway', icon: 'api', path: '/application-gateway' },
        { label: 'VPN Gateway', icon: 'vpn_lock', path: '/vpn-gateway' },
        { label: 'Azure Bastion', icon: 'castle', path: '/bastion' },
      ],
    },
    {
      label: 'Security & Ops',
      icon: 'security',
      items: [
        { label: 'Log Analytics', icon: 'analytics', path: '/log-analytics' },
        { label: 'Key Vault', icon: 'vpn_key', path: '/key-vault' },
        { label: 'Azure DevOps', icon: 'construction', path: '/azure-devops' },
        { label: 'Site Recovery', icon: 'health_and_safety', path: '/site-recovery' },
      ],
    },
    {
      label: 'Tools',
      icon: 'build',
      items: [
        { label: 'AI Assistant', icon: 'smart_toy', path: '/' },
        { label: 'KQL Query Builder', icon: 'terminal', path: '/kql-builder' },
        { label: 'Guided Estimator', icon: 'auto_awesome', path: '/guided' },
      ],
    },
  ];

  // Determine which groups should be expanded based on active path
  const initializeOpenStates = () => {
    const states: Record<string, boolean> = {};
    calcGroups.forEach(group => {
      const groupKey = group.label.toLowerCase().replace(/\s+/g, '-');
      if (collapsedGroups[groupKey] === false) {
        states[groupKey] = false;
      } else {
        // Default: group is open if it contains active path, otherwise follow stored state
        const hasActive = group.items.some(item => item.path === location.pathname);
        states[groupKey] = collapsedGroups[groupKey] !== undefined ? !collapsedGroups[groupKey] : hasActive || true;
      }
    });
    return states;
  };

  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>(initializeOpenStates());

  const toggleGroup = (groupKey: string) => {
    setOpenGroups(prev => {
      const newState = { ...prev, [groupKey]: !prev[groupKey] };
      setCollapsedGroups(prev => ({
        ...prev,
        [groupKey]: !newState[groupKey]
      }));
      return newState;
    });
  };

  // Top-level Calculators link
  const calculatorsLink: NavItem = {
    label: 'Calculators',
    icon: 'calculate',
    path: '/calculators',
  };

  // Learn sections
  const learnGroups: CalcGroup[] = [
    {
      label: 'Azure Pricing Guides',
      icon: 'menu_book',
      items: [
        { label: 'Azure Billing Traps UK', icon: 'warning', path: '/guides/azure-billing-traps-uk' },
        { label: 'Invoice Reconciliation', icon: 'verified', path: '/guides/invoice-reconciliation' },
        { label: 'Prompt Answers (AI-Optimized)', icon: 'smart_toy', path: '/prompt-answers' },
        { label: 'Azure Networking Hidden Costs UK', icon: 'article', path: '/guides/azure-networking-costs-uk' },
        { label: 'Azure Data Factory Pricing UK', icon: 'article', path: '/guides/azure-data-factory-pricing-uk' },
        { label: 'Azure Front Door Pricing UK', icon: 'article', path: '/guides/azure-front-door-pricing-uk' },
        { label: 'Azure SQL Database Pricing UK', icon: 'article', path: '/guides/azure-sql-pricing-uk' },
        { label: 'Azure OpenAI Pricing UK', icon: 'article', path: '/guides/azure-openai-pricing-uk' },
        { label: 'Log Analytics Pricing UK', icon: 'article', path: '/guides/azure-log-analytics-pricing-uk' },
        { label: 'Blob Storage Pricing UK', icon: 'article', path: '/guides/azure-blob-storage-pricing-uk' },
        { label: 'Bandwidth Costs Explained', icon: 'article', path: '/guides/azure-egress-bandwidth-costs-explained' },
        { label: 'Search Job vs Restore', icon: 'article', path: '/guides/log-analytics-search-job-vs-restore' },
      ],
    },
    {
      label: 'Certifications',
      icon: 'workspace_premium',
      items: [
        { label: 'SC-200 Study Guide UK', icon: 'school', path: '/certifications/sc-200-study-guide-uk' },
        { label: 'AZ-900 Study Guide UK', icon: 'school', path: '/certifications/az-900-study-guide-uk' },
        { label: 'AZ-104 Study Guide UK', icon: 'school', path: '/certifications/az-104-study-guide-uk' },
        { label: 'AZ-500 Study Guide UK', icon: 'school', path: '/certifications/az-500-study-guide-uk' },
      ],
    },
  ];

  const learnSingleItems: NavItem[] = [
    { label: 'FAQ & Resources', icon: 'help', path: '/faq' },
  ];

  // Account section
  const accountNavItems: NavItem[] = [
    { label: 'Changelog', icon: 'update', path: '/changelog' },
    { label: 'Saved Estimates', icon: 'save', path: '/saved' },
    { label: 'Price History', icon: 'history', path: '/history' },
    { label: 'Settings', icon: 'settings', path: '/settings' },
    { label: 'About', icon: 'info', path: '/about' },
  ];

  return (
    <>
      {/* Mobile overlay backdrop */}
      {mobileOpen && (
        <div
          className="fixed inset-0 z-40 md:hidden"
          style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
          onClick={onMobileClose}
          aria-hidden="true"
        />
      )}

      <aside
        className={`h-screen w-64 fixed left-0 top-0 flex flex-col overflow-y-auto custom-scrollbar z-50 transition-transform duration-200
          ${mobileOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0'}
          md:flex`}
        style={{ backgroundColor: 'var(--surface-container)' }}
      >
        {/* Sidebar header — logo, prices, mobile close */}
        <div
          className="flex items-center justify-between px-5 py-3 shrink-0"
          style={{ borderBottom: '1px solid var(--outline-variant)', minHeight: '56px' }}
        >
          <Link to="/" className="flex items-center gap-2 text-[#0078d4]" onClick={onMobileClose}>
            <span className="material-symbols-outlined text-primary-container text-[22px]" style={{ fontVariationSettings: "'FILL' 1" }}>
              deployed_code
            </span>
            <span className="font-headline font-semibold tracking-tight text-[15px]">AzureCalc.uk</span>
          </Link>
          <button
            onClick={onMobileClose}
            className="material-symbols-outlined p-1 rounded transition-all md:hidden"
            style={{ color: 'var(--text-tertiary)' }}
            aria-label="Close navigation menu"
          >
            close
          </button>
        </div>
        {pricesUpdated && (
          <div className="px-5 py-1.5 shrink-0" style={{ borderBottom: '1px solid var(--outline-variant)' }}>
            <span className="font-mono text-[9px] text-outline uppercase tracking-widest opacity-60">
              Prices updated: {pricesUpdated}
            </span>
          </div>
        )}

      <nav className="flex flex-col pt-2">

        {/* Calculators — top-level link */}
        <NavLink item={calculatorsLink} isActive={location.pathname === calculatorsLink.path} />

        {/* Calculator groups — collapsible */}
        <div className="mt-2 pt-2" style={{ borderTop: '1px solid var(--surface-container-high)' }}>
          {calcGroups.map((group) => {
            const groupKey = group.label.toLowerCase().replace(/\s+/g, '-');
            return (
              <CalcGroupSection
                key={group.label}
                group={group}
                activePathname={location.pathname}
                isOpen={openGroups[groupKey] ?? true}
                onToggle={() => toggleGroup(groupKey)}
              />
            );
          })}
        </div>

        {/* Learn section */}
        <div className="mt-2 pt-2" style={{ borderTop: '1px solid var(--surface-container-high)' }}>
          <p className="px-6 pt-2 pb-1 text-[9px] font-bold uppercase tracking-widest" style={{ color: 'var(--text-tertiary)' }}>Learn</p>
          {learnGroups.map((group) => (
            <CalcGroupSection
              key={group.label}
              group={group}
              activePathname={location.pathname}
              isOpen={openGroups[group.label.toLowerCase().replace(/\s+/g, '-')] ?? true}
              onToggle={() => toggleGroup(group.label.toLowerCase().replace(/\s+/g, '-'))}
            />
          ))}
          {learnSingleItems.map((item) => (
            <NavLink key={item.path} item={item} isActive={location.pathname === item.path} />
          ))}
        </div>

        {/* Account section */}
        <div className="mt-2 pt-2 pb-4" style={{ borderTop: '1px solid var(--surface-container-high)' }}>
          <p className="px-6 pt-2 pb-1 text-[9px] font-bold uppercase tracking-widest" style={{ color: 'var(--text-tertiary)' }}>Account</p>
          {accountNavItems.map((item) => (
            <NavLink key={item.path} item={item} isActive={location.pathname === item.path} />
          ))}
        </div>

      </nav>
    </aside>
    </>
  );
}
